import { api, mb, sayi, tarih, type PanoOperator, type PanoMusteri, type Kapasite } from "../api";
import { Bildirim, Durum, DurumRozeti, Kart, Rozet, Tablo, useCek } from "../ui";
import { Simge, type SimgeAdi } from "../simgeler";
import { Halka, Doluluk, Kivilcim, Isik, oranRengi } from "../grafik";

/**
 * PANO — panelin karsilama ekrani.
 *
 * Tasarim kurali: once DIKKAT ISTEYEN sey, sonra kapasite, sonra sayilar.
 * Bir yonetim panelinde ilk ekranin isi "her sey yolunda mi" sorusunu tek
 * bakista cevaplamaktir; rakamlar ondan sonra gelir.
 */
export function Pano({ git, rol }: { git: (y: string) => void; rol: "operator" | "musteri" | "kutu" }) {
  const pano = useCek(() => api.pano(), []);
  return (
    <Durum cekim={pano}>
      {(d) =>
        d.veri.rol === "operator" ? (
          <OperatorPano d={d.veri} git={git} yenile={pano.yenile} />
        ) : (
          <MusteriPano d={d.veri as PanoMusteri} git={git} yenile={pano.yenile} />
        )
      }
    </Durum>
  );
}

function selam(): string {
  const s = new Date().getHours();
  if (s < 6) return "İyi geceler";
  if (s < 12) return "Günaydın";
  if (s < 18) return "İyi günler";
  return "İyi akşamlar";
}

const ONEM_ROZET: Record<string, "iyi" | "uyari" | "kotu"> = {
  bilgi: "iyi",
  uyari: "uyari",
  kritik: "kotu",
};

/** Dikkat isteyen maddeler — her biri tiklanabilir ve nereye gidecegini soyler */
function DikkatListesi({
  liste,
  git,
}: {
  liste: PanoOperator["dikkat"];
  git: (y: string) => void;
}) {
  if (!liste.length) {
    return (
      <Kart>
        <div className="ic" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            aria-hidden
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "var(--ok-bg)",
              color: "var(--ok)",
              display: "grid",
              placeItems: "center",
              fontSize: 18,
              flex: "none",
            }}
          >
            ✓
          </span>
          <div>
            <strong>Her şey yolunda.</strong>
            <div style={{ color: "var(--n-600)", fontSize: 13 }}>
              Dikkat isteyen bir durum yok. Sistem kendi kendini 5 dakikada bir denetliyor.
            </div>
          </div>
        </div>
      </Kart>
    );
  }

  const kritik = liste.filter((x) => x.agirlik === "kritik");
  return (
    <div className="izgara d2">
      {[...kritik, ...liste.filter((x) => x.agirlik !== "kritik")].map((x) => (
        <button
          key={x.tur}
          className="dikkat-kart"
          onClick={() => git(x.yol)}
          data-agirlik={x.agirlik}
        >
          <div className="ust">
            <Rozet tur={ONEM_ROZET[x.agirlik]}>{x.agirlik}</Rozet>
            <strong>{x.baslik}</strong>
            <span className="adet">{x.adet}</span>
          </div>
          <p>{x.aciklama}</p>
          <span className="git">Aç →</span>
        </button>
      ))}
    </div>
  );
}

/** Kapasite cubugu — dolmaya yaklasan hemen goze carpsin */
function KapasiteSatiri({
  ad,
  kullanilan,
  limit,
  limitMetin,
  birim,
  oran,
}: {
  ad: string;
  kullanilan: number | string;
  /** Sayisal sinir; null = sinirsiz */
  limit?: number | null;
  /** Sinir zaten bicimlenmisse (ornegin "194 GB") bunu kullan */
  limitMetin?: string;
  birim?: string;
  oran: number;
}) {
  const sinirsiz = limitMetin === undefined && limit == null;
  return (
    <div style={{ marginBottom: 12 }}>
      <Doluluk
        oran={sinirsiz ? 0 : oran}
        etiket={ad}
        deger={
          <>
            <strong style={{ color: "var(--n-900)" }}>{kullanilan}</strong>
            {" / "}
            {limitMetin ?? (limit == null ? "∞" : limit)}
            {limitMetin ? "" : birim ? " " + birim : ""}
          </>
        }
      />
    </div>
  );
}

// --------------------------------------------------------------- operator

function OperatorPano({
  d,
  git,
  yenile,
}: {
  d: PanoOperator;
  git: (y: string) => void;
  yenile: () => void;
}) {
  const g = d.gostergeler;
  const fark = (bugun: number, dun: number) => {
    if (!dun) return null;
    const y = Math.round(((bugun - dun) / dun) * 100);
    return y === 0 ? null : y;
  };
  const gidenFark = fark(g.bugunGiden, g.dunGiden);

  return (
    <>
      <div className="baslik">
        <div>
          <h1>
            {selam()}, {d.kullanici.split("@")[0]}
          </h1>
          <p>
            {d.dikkat.length === 0
              ? "Sistemde dikkat isteyen bir durum yok."
              : `${d.dikkat.length} konu dikkatinizi bekliyor.`}{" "}
            {sayi(g.aktifMusteri)} aktif müşteri, {sayi(g.kutu)} posta kutusu.
          </p>
        </div>
        <div className="eylemler">
          <button className="dugme" onClick={yenile}>
            Yenile
          </button>
          <button className="dugme ana" onClick={() => git("/musteriler")}>
            Yeni müşteri
          </button>
        </div>
      </div>

      <DikkatListesi liste={d.dikkat} git={git} />

      <div style={{ height: 18 }} />

      <div className="izgara d4">
        <Olcuk
          ad="Müşteri"
          simge="musteri"
          deger={sayi(g.musteri)}
          alt={`${g.aktifMusteri} aktif`}
          tikla={() => git("/musteriler")}
        />
        <Olcuk
          ad="Alan adı"
          simge="alan"
          deger={sayi(g.alanAdi)}
          alt={`${d.saglik.dnsHazir} / ${d.saglik.alanAdi} DNS tamam`}
          uyari={d.saglik.dnsHazir < d.saglik.alanAdi}
          tikla={() => git("/alanadlari")}
        />
        <Olcuk
          ad="Posta kutusu"
          simge="kutu"
          deger={sayi(g.kutu)}
          alt={g.kilitliKutu ? `${g.kilitliKutu} kilitli` : "hepsi açık"}
          uyari={g.kilitliKutu > 0}
          tikla={() => git("/kutular")}
        />
        <Olcuk ad="Toplam alan" simge="sunucu" deger={mb(g.toplamDiskBayt)} alt="posta kutularında" />
      </div>

      <div style={{ height: 16 }} />

      <div className="izgara d2">
        <Kart baslik="Bugünkü trafik">
          <div className="ic">
            <div className="izgara d2" style={{ marginBottom: 14 }}>
              <Olcuk
                ad="Giden alıcı"
                simge="tasima"
                deger={sayi(g.bugunGiden)}
                alt={gidenFark !== null ? `düne göre %${gidenFark > 0 ? "+" : ""}${gidenFark}` : "dün veri yok"}
                seri={d.trafik.map((t) => t.giden)}
              />
              <Olcuk
                ad="Gelen ileti"
                simge="posta"
                deger={sayi(g.bugunGelen)}
                alt={`dün ${sayi(g.dunGelen)}`}
                seri={d.trafik.map((t) => t.gelen)}
              />
            </div>
            <Grafik seri={d.trafik} />
          </div>
        </Kart>

        <Kart baslik="Sistem sağlığı">
          <div className="ic">
            <SaglikSatiri
              ad="Son yedek"
              deger={
                d.saglik.yedek.saatOnce === null
                  ? "yok"
                  : `${d.saglik.yedek.saatOnce} saat önce`
              }
              iyi={d.saglik.yedek.saatOnce !== null && d.saglik.yedek.saatOnce <= 36}
            />
            {d.saglik.disk && (
              <div className="halka-satir" style={{ margin: "4px 0 14px" }}>
                <Halka
                  oran={d.saglik.disk.oran}
                  boyut={86}
                  ustYazi={`%${Math.round(d.saglik.disk.oran * 100)}`}
                  altYazi="dolu"
                />
                <div>
                  <div style={{ fontSize: 13, color: "var(--n-600)", marginBottom: 2 }}>Sunucu diski</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {mb(d.saglik.disk.bos)} <span style={{ color: "var(--n-500)", fontWeight: 400 }}>boş</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--n-500)", marginTop: 2 }}>
                    {mb(d.saglik.disk.kullanilan)} / {mb(d.saglik.disk.toplam)}
                  </div>
                  <div style={{ fontSize: 12, marginTop: 6, color: oranRengi(d.saglik.disk.oran).renk, fontWeight: 600 }}>
                    {oranRengi(d.saglik.disk.oran).durum}
                  </div>
                </div>
              </div>
            )}
            <SaglikSatiri
              ad="DNS'i tamam alan adı"
              deger={`${d.saglik.dnsHazir} / ${d.saglik.alanAdi}`}
              iyi={d.saglik.dnsHazir === d.saglik.alanAdi}
            />
          </div>
        </Kart>
      </div>

      <div style={{ height: 16 }} />

      <Kart
        baslik="Müşteri kapasiteleri"
        eylem={
          <button className="dugme kucuk" onClick={() => git("/musteriler")}>
            tümü
          </button>
        }
      >
        <div className="ic">
          {d.kapasite.length === 0 ? (
            <div className="bos">Henüz müşteri yok</div>
          ) : (
            <div className="izgara d2">
              {d.kapasite.map((k) => (
                <KapasiteKarti key={k.kiraciId} k={k} git={git} />
              ))}
            </div>
          )}
        </div>
      </Kart>

      <div style={{ height: 16 }} />

      <div className="izgara d2">
        <Kart baslik="En çok yer kaplayan kutular">
          {d.enBuyukKutular.length === 0 ? (
            <div className="bos">Kutu yok</div>
          ) : (
            <Tablo basliklar={["Adres", "#Boyut", "Durum"]}>
              {d.enBuyukKutular.map((k) => (
                <tr key={k.eposta}>
                  <td className="tek">{k.eposta}</td>
                  <td className="sayi">{mb(k.bayt)}</td>
                  <td>{k.kilitli ? <Rozet tur="uyari">kilitli</Rozet> : <Rozet tur="iyi">açık</Rozet>}</td>
                </tr>
              ))}
            </Tablo>
          )}
        </Kart>

        <Kart
          baslik="Bugün en çok gönderen"
          eylem={
            <button className="dugme kucuk" onClick={() => git("/guvenlik")}>
              güvenlik
            </button>
          }
        >
          {d.enCokGonderen.length === 0 ? (
            <div className="bos">Bugün gönderim yok</div>
          ) : (
            <Tablo basliklar={["Adres", "#Alıcı", "#Posta"]}>
              {d.enCokGonderen.map((k) => (
                <tr key={k.eposta}>
                  <td className="tek">{k.eposta}</td>
                  <td className="sayi">{sayi(k.giden_alici)}</td>
                  <td className="sayi">{sayi(k.giden_ileti)}</td>
                </tr>
              ))}
            </Tablo>
          )}
        </Kart>
      </div>

      <div style={{ height: 16 }} />

      <Kart
        baslik="Son işlemler"
        eylem={
          <button className="dugme kucuk" onClick={() => git("/denetim")}>
            tümü
          </button>
        }
      >
        <Tablo basliklar={["İşlem", "Kim", "Durum", "Zaman"]}>
          {d.sonIslemler.map((s, i) => (
            <tr key={i}>
              <td>
                <strong>{s.eylem}</strong>
              </td>
              <td style={{ color: "var(--n-600)" }}>{s.kullanici}</td>
              <td>
                {s.durum === "bitti" ? (
                  <Rozet tur="iyi">bitti</Rozet>
                ) : s.durum === "hata" ? (
                  <Rozet tur="kotu">hata</Rozet>
                ) : (
                  <Rozet tur="uyari">başladı</Rozet>
                )}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>{tarih(s.baslangic)}</td>
            </tr>
          ))}
        </Tablo>
      </Kart>
    </>
  );
}

/**
 * Dar bogaz halkasi.
 *
 * Bir musterinin uc siniri var (kutu, disk, gunluk gonderim) ve operatorun
 * merak ettigi sey "diski yuzde kac dolu" degil, "hangisi once bitecek".
 * Bu yuzden halka UC ORANDAN EN YUKSEGINI gosterir ve hangisi oldugunu yazar.
 *
 * Onceki surum diski gosteriyordu; 0.1 MB / 10 GB oraninda halka "0" yaziyor
 * ve bos bir cember gibi duruyordu — dogru sayi, yanlis soru.
 */
function DarBogaz({ k }: { k: Kapasite }) {
  const adaylar: Array<{ ad: string; oran: number }> = [
    { ad: "kutu", oran: k.kutu.oran },
    { ad: "disk", oran: k.disk.oran },
    { ad: "gönderim", oran: k.gonderim.oran },
  ];
  const en = adaylar.reduce((a, b) => (b.oran > a.oran ? b : a));
  const sinirsiz = k.kutu.limit == null && k.disk.limitMb == null && k.gonderim.limit == null;
  return (
    <Halka
      oran={sinirsiz ? 0 : en.oran}
      boyut={58}
      kalinlik={7}
      ustYazi={sinirsiz ? "∞" : `%${Math.round(en.oran * 100)}`}
      altYazi={sinirsiz ? undefined : en.ad}
      esik={0.8}
    />
  );
}

function KapasiteKarti({ k, git }: { k: Kapasite; git: (y: string) => void }) {
  return (
    <div className="kart" style={{ padding: "var(--sp-4)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 14.5 }}>{k.ad}</strong>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <Rozet tur="marka">{k.paket}</Rozet>
            <DurumRozeti durum={k.durum} />
            {k.kilitliKutu > 0 && <Rozet tur="uyari">{k.kilitliKutu} kilitli</Rozet>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <DarBogaz k={k} />
          <button className="dugme kucuk" onClick={() => git("/musteriler/" + k.kiraciId)}>
            aç
          </button>
        </div>
      </div>
      <KapasiteSatiri ad="Posta kutusu" kullanilan={k.kutu.kullanilan} limit={k.kutu.limit} oran={k.kutu.oran} />
      <KapasiteSatiri
        ad="Disk"
        kullanilan={k.disk.kullanilanMb.toFixed(1)}
        limit={k.disk.limitMb}
        birim="MB"
        oran={k.disk.oran}
      />
      <KapasiteSatiri
        ad="Bugünkü gönderim"
        kullanilan={k.gonderim.kullanilan}
        limit={k.gonderim.limit}
        birim="alıcı"
        oran={k.gonderim.oran}
      />
    </div>
  );
}

/**
 * Olcu karti.
 *
 * Bir sayi tek basina "cok mu az mi" sorusuna cevap vermez. Yaninda bir
 * kivilcim egrisi varsa okuyucu egilimi tek bakista gorur. Kart tiklanabilir
 * oldugunda bunu GORSEL olarak da soyler (hafif yukselme) — yoksa kullanici
 * tiklanabildigini denemeden bilemez.
 */
function Olcuk({
  ad,
  deger,
  alt,
  uyari,
  simge,
  seri,
  tikla,
}: {
  ad: string;
  deger: string | number;
  alt?: string;
  uyari?: boolean;
  simge?: SimgeAdi;
  seri?: number[];
  tikla?: () => void;
}) {
  const icerik = (
    <>
      <div className="olcu-ust">
        {simge && (
          <span className="olcu-simge" aria-hidden>
            <Simge ad={simge} boyut={15} />
          </span>
        )}
        <span className="ad">{ad}</span>
      </div>
      <div className="olcu-seri">
        <div>
          <div className="deger">{deger}</div>
          {alt && (
            <div className="not" style={uyari ? { color: "var(--warn)", fontWeight: 600 } : undefined}>
              {alt}
            </div>
          )}
        </div>
        {seri && seri.length > 1 && (
          <Kivilcim veri={seri} genislik={84} yukseklik={28} renk={uyari ? "var(--warn)" : "var(--brand)"} />
        )}
      </div>
    </>
  );

  if (!tikla) return <div className="kart olcu">{icerik}</div>;
  return (
    <div
      className="kart olcu tiklanir"
      role="button"
      tabIndex={0}
      onClick={tikla}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          tikla();
        }
      }}
    >
      {icerik}
    </div>
  );
}

function SaglikSatiri({ ad, deger, iyi }: { ad: string; deger: string; iyi: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
      <Isik durum={iyi ? "iyi" : "kotu"} yazi={<span style={{ color: "var(--n-600)" }}>{ad}</span>} />
      <span style={{ fontSize: 13, fontWeight: 600, color: iyi ? "var(--n-900)" : "var(--bad)" }}>{deger}</span>
    </div>
  );
}

/**
 * Gunluk trafik — iki serili sutun grafigi.
 *
 * Onceki surumde bos gunler hicbir iz birakmiyordu; iki haftanin on gunu bos
 * olunca grafik "bozuk" gibi gorunuyordu. Simdi hafif yatay kilavuz cizgileri
 * ve her gun icin bir taban var: bos gun de bir GUN oldugunu belli ediyor.
 */
function Grafik({ seri }: { seri: Array<{ tarih: string; giden: number; gelen: number }> }) {
  if (!seri.length) return null;
  const enBuyuk = Math.max(1, ...seri.map((s) => Math.max(s.giden, s.gelen)));
  const G = 100;
  const Y = 44;
  const n = seri.length;
  const bosluk = 1.4;
  const grup = (G - bosluk * (n - 1)) / n;
  const w = grup / 2 - 0.25;

  // Kilavuz cizgileri: 0, yarim, tam. Sayi degeri en ustte yazili.
  const cizgiler = [0, 0.5, 1];

  return (
    <div>
      <div style={{ position: "relative" }}>
        <svg viewBox={`0 0 ${G} ${Y}`} preserveAspectRatio="none" style={{ width: "100%", height: 96, display: "block" }}>
          {cizgiler.map((c) => (
            <line
              key={c}
              x1="0"
              x2={G}
              y1={Y - c * (Y - 4)}
              y2={Y - c * (Y - 4)}
              stroke="var(--n-200)"
              strokeWidth="0.4"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {seri.map((s, i) => {
            const x = i * (grup + bosluk);
            const hg = (s.giden / enBuyuk) * (Y - 5);
            const hl = (s.gelen / enBuyuk) * (Y - 5);
            return (
              <g key={s.tarih}>
                <title>{`${s.tarih}\ngiden ${s.giden} · gelen ${s.gelen}`}</title>
                {/* Bos gun de bir gundur: 0.7 birimlik taban iz birakir */}
                <rect x={x} y={Y - Math.max(hg, 0.7)} width={w} height={Math.max(hg, 0.7)} rx="0.6"
                      fill={s.giden ? "var(--brand)" : "var(--n-200)"} />
                <rect x={x + w + 0.5} y={Y - Math.max(hl, 0.7)} width={w} height={Math.max(hl, 0.7)} rx="0.6"
                      fill={s.gelen ? "var(--dv-3)" : "var(--n-200)"} />
              </g>
            );
          })}
        </svg>
        <span
          style={{
            position: "absolute", top: -2, right: 0, fontSize: 10.5, color: "var(--n-500)",
            fontVariantNumeric: "tabular-nums", background: "var(--n-0)", padding: "0 3px",
          }}
        >
          {enBuyuk}
        </span>
      </div>
      <div style={{ display: "flex", gap: 14, marginTop: 9, fontSize: 12, color: "var(--n-500)", alignItems: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <i style={{ width: 9, height: 9, background: "var(--brand)", borderRadius: 2 }} /> giden
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <i style={{ width: 9, height: 9, background: "var(--dv-3)", borderRadius: 2 }} /> gelen
        </span>
        <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 11 }}>
          {seri[0]?.tarih.slice(5)} — {seri[seri.length - 1]?.tarih.slice(5)}
        </span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- musteri

function MusteriPano({
  d,
  git,
  yenile,
}: {
  d: PanoMusteri;
  git: (y: string) => void;
  yenile: () => void;
}) {
  const k = d.kapasite;
  return (
    <>
      <div className="baslik">
        <div>
          <h1>
            {selam()}
            {d.kurum ? `, ${d.kurum}` : ""}
          </h1>
          <p>
            {d.dikkat.length === 0
              ? "Posta sisteminiz sorunsuz çalışıyor."
              : `${d.dikkat.length} konu dikkatinizi bekliyor.`}
          </p>
        </div>
        <div className="eylemler">
          <button className="dugme" onClick={yenile}>
            Yenile
          </button>
          <button className="dugme ana" onClick={() => git("/kutular")}>
            Yeni posta kutusu
          </button>
        </div>
      </div>

      <DikkatListesi liste={d.dikkat} git={git} />

      <div style={{ height: 18 }} />

      {k && (
        <Kart baslik={`Paketiniz: ${d.paket}`}>
          <div className="ic">
            <div className="izgara d2">
              <div>
                <KapasiteSatiri
                  ad="Posta kutusu"
                  kullanilan={k.kutu.kullanilan}
                  limit={k.kutu.limit}
                  oran={k.kutu.oran}
                />
                <KapasiteSatiri
                  ad="Disk alanı"
                  kullanilan={k.disk.kullanilanMb.toFixed(1)}
                  limit={k.disk.limitMb}
                  birim="MB"
                  oran={k.disk.oran}
                />
              </div>
              <div>
                <KapasiteSatiri
                  ad="Bugünkü gönderim"
                  kullanilan={k.gonderim.kullanilan}
                  limit={k.gonderim.limit}
                  birim="alıcı"
                  oran={k.gonderim.oran}
                />
                <KapasiteSatiri
                  ad="Alan adı"
                  kullanilan={k.alanAdi.kullanilan}
                  limit={k.alanAdi.limit}
                  oran={k.alanAdi.oran}
                />
              </div>
            </div>
          </div>
        </Kart>
      )}

      <div style={{ height: 16 }} />

      <div className="izgara d2">
        <Kart
          baslik={`Posta kutularınız (${d.kutular.length})`}
          eylem={
            <button className="dugme kucuk" onClick={() => git("/kutular")}>
              yönet
            </button>
          }
        >
          {d.kutular.length === 0 ? (
            <div className="bos">Henüz posta kutusu yok</div>
          ) : (
            <Tablo basliklar={["Adres", "#Boyut", "Durum"]}>
              {d.kutular.map((x) => (
                <tr key={x.id}>
                  <td className="tek">{x.eposta}</td>
                  <td className="sayi">{mb(x.bayt)}</td>
                  <td>
                    <DurumRozeti durum={x.durum} />
                  </td>
                </tr>
              ))}
            </Tablo>
          )}
        </Kart>

        <Kart baslik="Alan adlarınız">
          {d.alanAdlari.length === 0 ? (
            <div className="bos">Alan adı yok</div>
          ) : (
            <Tablo basliklar={["Alan adı", "DNS", "Son kontrol"]}>
              {d.alanAdlari.map((a) => (
                <tr key={a.id}>
                  <td className="tek">{a.ad}</td>
                  <td>
                    {a.hazir ? <Rozet tur="iyi">tamam</Rozet> : <Rozet tur="kotu">eksik</Rozet>}
                  </td>
                  <td style={{ whiteSpace: "nowrap", color: "var(--n-600)" }}>{tarih(a.sonKontrol)}</td>
                </tr>
              ))}
            </Tablo>
          )}
        </Kart>
      </div>

      {d.silmeBekleyen.length > 0 && (
        <>
          <div style={{ height: 16 }} />
          <Bildirim tur="uyari">
            <strong>{d.silmeBekleyen.length} kutu silinmeyi bekliyor.</strong>{" "}
            {d.silmeBekleyen.map((s) => `${s.eposta} (${s.kalanGun} gün)`).join(", ")} — bu süre
            içinde geri alabilirsiniz, hiçbir posta kaybolmaz.
          </Bildirim>
        </>
      )}

      <div style={{ height: 16 }} />

      <Kart baslik="Son 14 gün">
        <div className="ic">
          <Grafik seri={d.trafik} />
        </div>
      </Kart>
    </>
  );
}
